import { useEffect, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';

import { Box, Button, Flex } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { AvatarType, Size } from '../../Avatar/Avatar';
import StackedAvatar from '../../StackedAvatar/StackedAvatar';
import { ShortlistContactButtonClickEvent, ShortlistOpenEvent } from '../analyticsEvents';
import { MAX_PREVIEW_AVATARS, MULTI_CONTACT_ROUTE } from '../constants';
import useShortlist from '../hooks/useShortlist';
import { useShortlistPreviewTarget } from '../ShortlistPreviewTargetContext';

import ShortlistPreviewPill from './ShortlistPreviewPill';

export const buildPreviewImageUrls = (count: number, loadedImageUrls: string[]): string[] => {
  const slots = Math.min(count, MAX_PREVIEW_AVATARS);
  const loaded = loadedImageUrls.slice(0, slots);
  // `count` comes from storage and is right on the first paint, while a sitter's photo arrives
  // with a later fetch. Holding a slot for each one keeps the row from shifting as they land.
  return [...loaded, ...Array.from({ length: slots - loaded.length }, () => '')];
};

/** How far short of the release point the backdrop has already reached full opacity. */
export const BACKDROP_FADE_OFFSET_PX = 50;

/** How much scrolling the backdrop fades in over, measured back from that full-opacity point. */
export const BACKDROP_FADE_DISTANCE_PX = 150;

/**
 * Backdrop opacity for a given distance, in px, between the scroll container's bottom edge and
 * the viewport bottom, the closer to the bottom, the more opaque the backdrop.
 */
export const computeBackdropOpacity = (distanceToRelease: number): number => {
  const progress = 1 - (distanceToRelease - BACKDROP_FADE_OFFSET_PX) / BACKDROP_FADE_DISTANCE_PX;
  return Math.min(1, Math.max(0, progress));
};

/**
 * Browsers focus a button on tap and on click too, and the modal hands focus back
 * to its trigger on close. None of that should pin the preview in place for
 * someone who is not navigating by keyboard. Where the selector is unsupported,
 * assume keyboard: that is the case the latch exists to protect.
 */
const isKeyboardFocus = (target: EventTarget | null): boolean => {
  try {
    return (target as Element).matches(':focus-visible');
  } catch {
    return true;
  }
};

/** Handlers the router puts on whichever variant renders, to run that latch. */
export type ShortlistPreviewVariantProps = {
  focusProps: {
    onFocus: (event: React.FocusEvent<HTMLElement>) => void;
    onBlur: (event: React.FocusEvent<HTMLElement>) => void;
  };
};

/** The desktop preview: a sticky bottom bar with a Contact all CTA. */
const ShortlistPreviewBar = ({ focusProps }: ShortlistPreviewVariantProps): JSX.Element => {
  const prefersReducedMotion = useMatchMedia('(prefers-reduced-motion: reduce)');
  const { count, getSitters, setIsModalVisible } = useShortlist();
  const { registerTarget } = useShortlistPreviewTarget();
  const theme = useTheme();
  const [isAnimating, setIsAnimating] = useState(false);

  const prevCount = useRef(count);
  useEffect(() => {
    if (count > prevCount.current && !prefersReducedMotion) {
      setIsAnimating(true);
    }
    prevCount.current = count;
  }, [count, prefersReducedMotion]);

  // The backdrop fades in as the bar nears its sticky release point at the end of the scroll
  // container, so the grey band only appears where it replaces the page's own background.
  // Opacity is written straight to the node: this runs once per animation frame while scrolling,
  // and routing it through state would re-render the whole bar that often.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const container = wrapperRef.current?.parentElement;
    if (!container) {
      return undefined;
    }

    let frame = 0;
    const update = (): void => {
      frame = 0;
      const backdrop = backdropRef.current;
      if (backdrop) {
        const distanceToRelease = container.getBoundingClientRect().bottom - window.innerHeight;
        backdrop.style.opacity = String(computeBackdropOpacity(distanceToRelease));
      }
    };
    const schedule = (): void => {
      if (!frame) {
        frame = window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
    // `count` changes the bar's height, which moves the container's bottom edge.
  }, [count]);

  const pulseAnimationStyles = {
    '@keyframes shortlistPulse': {
      '0%': { transform: 'scale(1)', backgroundColor: theme.background.primary },
      '30%': { transform: 'scale(1.05)', backgroundColor: theme.background.secondary },
      '60%': { transform: 'scale(0.98)', backgroundColor: theme.background.primary },
      '100%': { transform: 'scale(1)', backgroundColor: theme.background.primary },
    },
    animation: 'shortlistPulse 0.4s ease-in-out',
  };

  const loadedImageUrls = getSitters()
    .map((sitter) => sitter.defaultImage.medium || sitter.defaultImage.small)
    .filter(Boolean);
  const imageUrls = buildPreviewImageUrls(count, loadedImageUrls);

  const handleLinkPress = (): void => {
    emitAnalyticsEvent(new ShortlistOpenEvent({ source: 'preview', count }));
    setIsModalVisible(true);
  };

  const handleButtonPress = (): void => {
    new ShortlistContactButtonClickEvent({ source: 'preview', count }).fireCrossPageEvent();
  };

  return (
    <Box
      ref={wrapperRef}
      position="sticky"
      bottom="0"
      width="100%"
      // Sit just below the FilterDropdown z-index (1000) so an open dropdown
      // covers the bar, not the other way round.
      zIndex="999"
      display="flex"
      justifyContent="center"
      p="4x"
      testID="shortlist-preview"
      sx={{ pointerEvents: 'none', borderRadius: '10x' }}
      webProps={focusProps}
    >
      <Box
        ref={backdropRef}
        position="absolute"
        top="0"
        right="0"
        bottom="0"
        left="0"
        background="secondary"
        testID="shortlist-preview-backdrop"
        sx={{ opacity: 0 }}
      />
      <Flex
        position="relative"
        width="100%"
        maxWidth="600px"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        gap="4x"
        background="primary"
        borderRadius="round"
        variant="elevation_mid"
        px="4x"
        py="4x"
        testID="shortlist-preview-card"
        sx={{
          pointerEvents: 'auto',
          ...(isAnimating ? pulseAnimationStyles : {}),
        }}
        webProps={{ onAnimationEnd: () => setIsAnimating(false) }}
      >
        <Flex flexDirection="row" alignItems="center" gap="3x" minWidth="0">
          {/* Decorative: the adjacent link already states the count, which the
              stack caps and so cannot state on its own. */}
          <Box
            ref={registerTarget}
            display="inline-flex"
            flexShrink="0"
            webProps={{ 'aria-hidden': true }}
          >
            <StackedAvatar imageUrls={imageUrls} avatarType={AvatarType.Person} size={Size.Small} />
          </Box>
          <Button variant="link" onPress={handleLinkPress} sx={{ color: 'text.primary' }}>
            <Trans>View contact list ({count})</Trans>
          </Button>
        </Flex>
        <Box width="auto" flexShrink="0">
          <Button
            variant="primary"
            fullWidth
            href={MULTI_CONTACT_ROUTE}
            onPress={handleButtonPress}
          >
            <Trans>Contact all</Trans>
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

type Props = {
  /** Hide the preview, unless it currently holds keyboard focus. */
  isSuppressed?: boolean;
};

const ShortlistPreview = ({ isSuppressed = false }: Props): JSX.Element | null => {
  // Branch in JS rather than with responsive `display` styles: a hidden variant
  // would still register its avatar stack as the flying-avatar target. The query
  // matches the 768px breakpoint the bar's former responsive styles broke at.
  const isMwebLayout = useMatchMedia(MQ.XS.getBreakpointString());
  const { isVisible, count } = useShortlist();

  // Unmounting a focused control drops focus to the document body, sending a
  // keyboard or screen reader user back to the top of the page, so once the
  // preview holds focus it stays put until focus moves off it.
  const [hasKeyboardFocus, setHasKeyboardFocus] = useState(false);
  // An emptied list tears the preview down without a blur ever reaching it,
  // which would otherwise leave the latch stuck on for the next render.
  useEffect(() => {
    if (!isVisible || count === 0) {
      setHasKeyboardFocus(false);
    }
  }, [isVisible, count]);

  if (!isVisible || count === 0 || isMwebLayout === undefined) {
    return null;
  }

  if (isSuppressed && !hasKeyboardFocus) {
    return null;
  }

  const focusProps: ShortlistPreviewVariantProps['focusProps'] = {
    onFocus: (event) => {
      if (isKeyboardFocus(event.target)) {
        setHasKeyboardFocus(true);
      }
    },
    // The bar holds two controls, so only focus leaving the whole thing counts.
    onBlur: (event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) {
        setHasKeyboardFocus(false);
      }
    },
  };

  return isMwebLayout ? (
    <ShortlistPreviewPill focusProps={focusProps} />
  ) : (
    <ShortlistPreviewBar focusProps={focusProps} />
  );
};

export default ShortlistPreview;
